import { type ApolloError } from 'apollo-client'
import { useCallback } from 'react'
import {
  type CollectionsFormData,
  CollectionsFormMode,
  type CollectionsFormModeProps,
} from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { useCreateCollection } from 'domains/collections/api/createCollection.hooks'
import {
  type CollectionSubjectInput,
  CollectionTypeEnum,
  type CreateCollectionMutationVariables,
} from 'domains/collections/api'
import {
  type CollectionRule,
  CollectionRuleType,
} from 'domains/collections/components/collections-form/components/rules/utils/CollectionsRules.types'
import { useUpdateCollection } from 'domains/collections/api/updateCollection.hooks'
import { CollectionsContentType } from 'domains/collections/components/collections-form/components/content/CollectionsContent'
import { trackEvent } from 'utils/events/trackEvent'

interface UseCollectionsFormOnSubmitProps extends CollectionsFormModeProps {
  contentType: CollectionsContentType
}

interface UseCollectionsFormSubmitResult {
  submitForm: (formData: CollectionsFormData) => void
  formSubmitSuccess: boolean
  formSubmitError?: ApolloError
  formSubmitLoading: boolean
}

const mapRulesToSubjectData = (rule: CollectionRule): CollectionSubjectInput => {
  if (rule.type === CollectionRuleType.productCodes) {
    return {
      subjectName: rule.name,
      criteriaProductIds: rule.productIds,
      subjectId: rule.subjectId,
    }
  }

  if (rule.type === CollectionRuleType.searchTerm) {
    return {
      subjectName: rule.name,
      criteriaTerms: [rule.searchTerm],
      subjectId: rule.subjectId,
    }
  }
}
export const mapFormDataToInput = (
  formData: CollectionsFormData,
  contentType: CollectionsContentType
): CreateCollectionMutationVariables => {
  // Always null if not a value
  const image = formData?.tile?.image || null
  const headerData = formData.header
    ? {
        title: formData.header.title,
        displayStyle: formData.header.displayStyle,
        description: formData.header.description,
        backgroundColor: formData.header.backgroundColor,
        color: formData.header.color,
        imageMobile: formData.header.mobileImage,
        imageWeb: formData.header.webImage,
      }
    : null

  const storefrontBannerData = formData.storefrontBanner
    ? {
        active: formData.storefrontBanner.active || false,
        heading: formData.storefrontBanner.heading,
        headingColor: formData.storefrontBanner.headingColor,
        subHeading: formData.storefrontBanner.subHeading,
        subHeadingColor: formData.storefrontBanner.subHeadingColor,
        tag: formData.storefrontBanner.tag,
        tagColor: formData.storefrontBanner.tagColor,
        cta: formData.storefrontBanner.cta,
        backgroundColor: formData.storefrontBanner.backgroundColor,
        image: formData.storefrontBanner.bannerImage,
      }
    : null

  return {
    // details
    collectionName: formData.details.name,
    startDate: formData.details.startDate,
    endDate: formData.details.endDate,
    retailerIds: formData.details.retailerIds,
    parentId: formData.details.parentId,
    active: formData.details.active,
    activeAdminOnly: formData.details.activeAdminOnly,
    // tile
    image,
    // header
    headerData,
    // storefront banner
    storefrontBannerData,
    // defaults
    collectionType: CollectionTypeEnum.Holiday, // default for now

    subjectsData:
      contentType === CollectionsContentType.rules
        ? formData.rules?.map(mapRulesToSubjectData)
        : [],
    childrenIds:
      contentType === CollectionsContentType.childCollections ? formData.childrenIds : [],
  }
}

/**
 * Handles the submission of the updated form values to the server.
 *
 * Normalizes the form data, then triggers the appropriate API call based on
 * the current mode.
 */
export const useCollectionsFormOnSubmit = ({
  formModeDetails,
  contentType,
}: UseCollectionsFormOnSubmitProps): UseCollectionsFormSubmitResult => {
  const { updateCollection, ...updateCollectionRest } = useUpdateCollection()
  const { createCollection, ...createCollectionRest } = useCreateCollection()

  const submitForm = useCallback(
    (formData: CollectionsFormData) => {
      const submitVariables = mapFormDataToInput(formData, contentType)

      // if form is being used from a specific retailer (ie: not admin page)
      // set retailerIds to only itself
      // admin mode has a dropdown selector to choose multiple retailers
      if (formModeDetails.retailerId) {
        submitVariables.retailerIds = [formModeDetails.retailerId]
      }

      if (formModeDetails.formMode === CollectionsFormMode.Create) {
        const collectionCreateData = {
          ...submitVariables,
        }

        trackEvent({
          id: 'collections.create',
          description: 'Creating a collection',
          data: { collectionCreateData },
        })
        createCollection(collectionCreateData)
      }

      if (formModeDetails.formMode === CollectionsFormMode.Update) {
        const collectionUpdateData = {
          ...submitVariables,
          collectionId: formModeDetails.collectionId,
        }

        trackEvent({
          id: 'collections.update',
          description: 'Updating a collection',
          data: { collectionUpdateData },
        })

        updateCollection(collectionUpdateData)
      }
    },
    [
      createCollection,
      updateCollection,
      formModeDetails.formMode,
      formModeDetails.collectionId,
      formModeDetails.retailerId,
      contentType,
    ]
  )

  if (formModeDetails.formMode === CollectionsFormMode.Create) {
    return {
      submitForm,
      formSubmitSuccess: Boolean(createCollectionRest.apiSuccess),
      formSubmitError: createCollectionRest.apiError,
      formSubmitLoading: createCollectionRest.apiLoading,
    }
  }

  if (formModeDetails.formMode === CollectionsFormMode.Update) {
    return {
      submitForm,
      formSubmitSuccess: Boolean(updateCollectionRest.apiSuccess),
      formSubmitError: updateCollectionRest.apiError,
      formSubmitLoading: updateCollectionRest.apiLoading,
    }
  }

  return {
    submitForm,
    formSubmitSuccess: false,
    formSubmitError: undefined,
    formSubmitLoading: false,
  }
}
